import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AnnouncementAudienceInput,
  AnnouncementAudiencePeopleInput,
  AnnouncementReach,
  AudiencePeoplePage,
  AnnouncementCreateInput,
  AnnouncementDetail,
  AnnouncementListItem,
  AnnouncementPreviewInput,
  ActivityCancelInput,
  ActivityCreateInput,
  ActivityDetail,
  ActivityReportInput,
  ActivityListItem,
  ActivityUpdateInput,
  AuditLogItem,
  BranchImportResult,
  CategoryAdminItem,
  CategoryOption,
  ConcernListItem,
  ConcernSummary,
  ConcernTopicAdminItem,
  CountyDto,
  DashboardSummary,
  MessageCreateInput,
  MessageDto,
  MessageUpdateInput,
  NamedRef,
  UserGroup,
  MunicipalityDto,
  NotificationPage,
  SessionDto,
  Paginated,
  PersonProfile,
  PersonRef,
  StructureCounty,
  UnitDto,
  UserCreateInput,
  UserListItem,
  UserUpdateInput,
} from '@pd/shared';
import { api } from './client';

const STATIC = { staleTime: 60 * 60 * 1000 };

// ─── Reference data ──────────────────────────────────────────────────────
export const useCounties = () =>
  useQuery({
    queryKey: ['counties'],
    queryFn: () => api.get<CountyDto[]>('/geo/counties'),
    ...STATIC,
  });

export const useMunicipalities = (countyId?: string | null) =>
  useQuery({
    queryKey: ['municipalities', countyId],
    queryFn: () => api.get<MunicipalityDto[]>(`/geo/counties/${countyId}/municipalities`),
    enabled: !!countyId,
    ...STATIC,
  });

export const useUnits = (municipalityId?: string | null) =>
  useQuery({
    queryKey: ['units', municipalityId],
    queryFn: () => api.get<UnitDto[]>(`/geo/municipalities/${municipalityId}/units`),
    enabled: !!municipalityId,
    ...STATIC,
  });

/** Active categories with the groups they are offered to (filter with `categoryVisibleTo`). */
export const useCategories = () =>
  useQuery({
    queryKey: ['categories'],
    queryFn: () => api.get<CategoryOption[]>('/categories'),
    ...STATIC,
  });

export const useConcernTopics = () =>
  useQuery({
    queryKey: ['concern-topics'],
    queryFn: () => api.get<NamedRef[]>('/concern-topics'),
    ...STATIC,
  });

// ─── Activities ──────────────────────────────────────────────────────────
export type ActivityFilters = Record<string, string | number | undefined>;

export const useActivities = (filters: ActivityFilters, { enabled = true } = {}) =>
  useQuery({
    enabled,
    queryKey: ['activities', filters],
    queryFn: () => api.get<Paginated<ActivityListItem>>('/activities', filters),
    placeholderData: keepPreviousData,
  });

/**
 * `watchMedia` refetches every few seconds while a video is still being converted.
 * Leave it off in forms, which copy the data into local state once.
 */
export const useActivity = (id?: string, { watchMedia = false } = {}) =>
  useQuery({
    queryKey: ['activity', id],
    queryFn: () => api.get<ActivityDetail>(`/activities/${id}`),
    enabled: !!id,
    refetchInterval: (q) =>
      watchMedia && q.state.data?.media.some((m) => m.status === 'PROCESSING') ? 5000 : false,
  });

export function useSaveActivity(id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ActivityCreateInput | ActivityUpdateInput) =>
      id
        ? api.patch<ActivityDetail>(`/activities/${id}`, body)
        : api.post<ActivityDetail>('/activities', body),
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ['activities'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      qc.setQueryData(['activity', a.id], a);
    },
  });
}

function useActivityAction<T>(path: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: T }) =>
      path === 'report'
        ? api.put<ActivityDetail>(`/activities/${id}/report`, body)
        : api.post<ActivityDetail>(`/activities/${id}/${path}`, body),
    onSuccess: (a) => {
      for (const k of ['activities', 'dashboard', 'notifications', 'messages', 'concerns'])
        qc.invalidateQueries({ queryKey: [k] });
      qc.setQueryData(['activity', a.id], a);
    },
  });
}

/** Files or corrects the report of an activity. */
export const useReportActivity = () => useActivityAction<ActivityReportInput>('report');
/** Cancels an activity, with a short reason. */
export const useCancelActivity = () => useActivityAction<ActivityCancelInput>('cancel');

export function useDeleteActivity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/activities/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['activities'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

// ─── Dashboard ───────────────────────────────────────────────────────────
export const useDashboard = (filters: {
  from?: string;
  to?: string;
  countyId?: string;
  group?: UserGroup;
}) =>
  useQuery({
    queryKey: ['dashboard', filters],
    queryFn: () => api.get<DashboardSummary>('/dashboard/summary', filters),
    placeholderData: keepPreviousData,
  });

// ─── Users ───────────────────────────────────────────────────────────────
export const useUsers = () =>
  useQuery({ queryKey: ['users'], queryFn: () => api.get<UserListItem[]>('/users') });

export function useSaveUser(id?: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UserCreateInput | UserUpdateInput) =>
      id ? api.patch<UserListItem>(`/users/${id}`, body) : api.post<UserListItem>('/users', body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

export const useResendInvite = () =>
  useMutation({ mutationFn: (id: string) => api.post(`/users/${id}/invite`) });

// ─── Audit ───────────────────────────────────────────────────────────────
export const useAuditLogs = (page: number) =>
  useQuery({
    queryKey: ['audit', page],
    queryFn: () => api.get<Paginated<AuditLogItem>>('/audit-logs', { page, pageSize: 50 }),
    placeholderData: keepPreviousData,
  });

// ─── Organisational structure (admin) ────────────────────────────────────
export type StructureKind = 'counties' | 'municipalities' | 'units';

export const useStructure = () =>
  useQuery({
    queryKey: ['structure'],
    queryFn: () => api.get<StructureCounty[]>('/admin/structure'),
  });

/** Create / update / delete any structure node, refreshing the admin tree and every picker. */
export function useStructureMutation() {
  const qc = useQueryClient();
  const refresh = () =>
    Promise.all(
      ['structure', 'counties', 'municipalities', 'units', 'users'].map((k) =>
        qc.invalidateQueries({ queryKey: [k] }),
      ),
    );
  return useMutation({
    mutationFn: (
      op:
        | { action: 'create'; kind: StructureKind; body: object }
        | { action: 'update'; kind: StructureKind; id: string; body: object }
        | { action: 'delete'; kind: StructureKind; id: string },
    ) => {
      const base = `/admin/structure/${op.kind}`;
      if (op.action === 'create') return api.post(base, op.body);
      if (op.action === 'update') return api.patch(`${base}/${op.id}`, op.body);
      return api.delete(`${base}/${op.id}`);
    },
    onSuccess: refresh,
  });
}

export function useImportBranches() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { csv: string; dryRun: boolean }) =>
      api.post<BranchImportResult>('/admin/structure/units/import', body),
    onSuccess: (r) => {
      if (!r.dryRun) {
        for (const k of ['structure', 'units']) qc.invalidateQueries({ queryKey: [k] });
      }
    },
  });
}

// ─── Activity categories (admin) ─────────────────────────────────────────
export const useCategoriesAdmin = () =>
  useQuery({
    queryKey: ['categories-admin'],
    queryFn: () => api.get<CategoryAdminItem[]>('/admin/categories'),
  });

export function useCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      op:
        | { action: 'create'; body: object }
        | { action: 'update'; id: string; body: object }
        | { action: 'move'; id: string; direction: 'up' | 'down' }
        | { action: 'delete'; id: string },
    ) => {
      if (op.action === 'create') return api.post('/admin/categories', op.body);
      if (op.action === 'update') return api.patch(`/admin/categories/${op.id}`, op.body);
      if (op.action === 'move') return api.post(`/admin/categories/${op.id}/move/${op.direction}`);
      return api.delete(`/admin/categories/${op.id}`);
    },
    onSuccess: () =>
      Promise.all(
        ['categories-admin', 'categories', 'activities', 'activity', 'dashboard'].map((k) =>
          qc.invalidateQueries({ queryKey: [k] }),
        ),
      ),
  });
}

// ─── Concern topics (admin) ──────────────────────────────────────────────
export const useConcernTopicsAdmin = () =>
  useQuery({
    queryKey: ['concern-topics-admin'],
    queryFn: () => api.get<ConcernTopicAdminItem[]>('/admin/concern-topics'),
  });

export function useConcernTopicMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      op:
        | { action: 'create'; body: object }
        | { action: 'update'; id: string; body: object }
        | { action: 'move'; id: string; direction: 'up' | 'down' }
        | { action: 'delete'; id: string },
    ) => {
      const base = '/admin/concern-topics';
      if (op.action === 'create') return api.post(base, op.body);
      if (op.action === 'update') return api.patch(`${base}/${op.id}`, op.body);
      if (op.action === 'move') return api.post(`${base}/${op.id}/move/${op.direction}`);
      return api.delete(`${base}/${op.id}`);
    },
    onSuccess: () =>
      Promise.all(
        ['concern-topics-admin', 'concern-topics', 'concerns'].map((k) =>
          qc.invalidateQueries({ queryKey: [k] }),
        ),
      ),
  });
}

// ─── Concerns heard from citizens (leadership) ───────────────────────────
export const useConcerns = (filters: ActivityFilters) =>
  useQuery({
    queryKey: ['concerns', 'list', filters],
    queryFn: () => api.get<Paginated<ConcernListItem>>('/concerns', filters),
    placeholderData: keepPreviousData,
  });

export const useConcernSummary = (filters: ActivityFilters) =>
  useQuery({
    queryKey: ['concerns', 'summary', filters],
    queryFn: () => api.get<ConcernSummary>('/concerns/summary', filters),
    placeholderData: keepPreviousData,
  });

// ─── Messages of the week ────────────────────────────────────────────────
export const useMessages = () =>
  useQuery({ queryKey: ['messages'], queryFn: () => api.get<MessageDto[]>('/messages') });

export function useMessageMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (
      op:
        | { action: 'create'; body: MessageCreateInput }
        | { action: 'update'; id: string; body: MessageUpdateInput }
        | { action: 'delete'; id: string },
    ) => {
      if (op.action === 'create') return api.post('/messages', op.body);
      if (op.action === 'update') return api.patch(`/messages/${op.id}`, op.body);
      return api.delete(`/messages/${op.id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messages'] }),
  });
}

// ─── People (tagging, person pages) ──────────────────────────────────────
export const usePeopleSearch = (q: string) =>
  useQuery({
    queryKey: ['people', q],
    queryFn: () => api.get<PersonRef[]>('/people', { q }),
    enabled: q.trim().length > 0,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });

export const usePerson = (id?: string) =>
  useQuery({
    queryKey: ['person', id],
    queryFn: () => api.get<PersonProfile>(`/people/${id}`),
    enabled: !!id,
  });

// ─── Announcements ───────────────────────────────────────────────────────
export const useAnnouncements = (page: number, pageSize: number) =>
  useQuery({
    queryKey: ['announcements', page, pageSize],
    queryFn: () => api.get<Paginated<AnnouncementListItem>>('/announcements', { page, pageSize }),
    placeholderData: keepPreviousData,
  });

/** While e-mails are still going out, the sender's view refreshes every few seconds. */
export const useAnnouncement = (id?: string) =>
  useQuery({
    queryKey: ['announcement', id],
    queryFn: () => api.get<AnnouncementDetail>(`/announcements/${id}`),
    enabled: !!id,
    refetchInterval: (q) => (q.state.data?.email && !q.state.data.email.finishedAt ? 3000 : false),
  });

export const useAnnouncementReach = (audience: AnnouncementAudienceInput) =>
  useQuery({
    queryKey: ['announcements', 'reach', audience],
    queryFn: () => api.post<AnnouncementReach>('/announcements/audience', audience),
    placeholderData: keepPreviousData,
  });

/** The audience by name, a page at a time (only fetched while the list is open). */
export const useAnnouncementAudiencePeople = (
  input: AnnouncementAudiencePeopleInput,
  enabled: boolean,
) =>
  useQuery({
    queryKey: ['announcements', 'audience-people', input],
    queryFn: () => api.post<AudiencePeoplePage>('/announcements/audience/people', input),
    enabled,
    placeholderData: keepPreviousData,
  });

export const useAnnouncementPreview = () =>
  useMutation({
    mutationFn: (body: AnnouncementPreviewInput) =>
      api.post<{ subject: string; html: string }>('/announcements/preview', body),
  });

export function useSendAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: AnnouncementCreateInput) =>
      api.post<AnnouncementDetail>('/announcements', body),
    onSuccess: (a) => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
      qc.setQueryData(['announcement', a.id], a);
    },
  });
}

// ─── Notifications ───────────────────────────────────────────────────────
/** Checked every minute and whenever the window regains focus; cheap (one count query). */
export const useUnreadNotifications = () =>
  useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    select: (d) => d.count,
  });

/** The latest few, for the bell's panel; fetched only while it is open. */
export const useLatestNotifications = (enabled: boolean) =>
  useQuery({
    queryKey: ['notifications', 'latest'],
    queryFn: () => api.get<NotificationPage>('/notifications', { page: 1, pageSize: 6 }),
    enabled,
  });

/** The full list, page by page ("Shfaq më shumë"). */
export const useNotificationPages = () =>
  useInfiniteQuery({
    queryKey: ['notifications', 'pages'],
    queryFn: ({ pageParam }) =>
      api.get<NotificationPage>('/notifications', { page: pageParam, pageSize: 20 }),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
  });

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | 'all') =>
      api.post(id === 'all' ? '/notifications/read-all' : `/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ─── Sessions ────────────────────────────────────────────────────────────
/** Where the current user is signed in; refreshed on focus so it stays truthful. */
export const useSessions = () =>
  useQuery({
    queryKey: ['sessions'],
    queryFn: () => api.get<SessionDto[]>('/auth/sessions'),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

export function useEndSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string | 'others') =>
      id === 'others'
        ? api.post('/auth/sessions/revoke-others')
        : api.delete(`/auth/sessions/${id}`),
    onSettled: () => qc.invalidateQueries({ queryKey: ['sessions'] }),
  });
}
